// ReportDateRangePicker.tsx
import { addDays, format, startOfDay, startOfMonth, startOfQuarter, endOfMonth, endOfQuarter, endOfYear, startOfYear, addMonths, addYears, subDays, subMonths, isSameDay, isLastDayOfMonth, isFirstDayOfMonth, differenceInDays, differenceInMonths } from "date-fns";
import React, { useEffect, useId, useState } from "react";

export interface DateRangeOption {
  label: string;
  start?: Date;
  end?: Date;
  isDivider?: boolean;
}

export interface ReportDateRangePickerProps {
  start: string;
  end: string;
  setStart: (value: string) => void;
  setEnd: (value: string) => void;
  selectedRange: string;
  setSelectedRange: (value: string) => void;
  allowLockPeriod?: boolean;
  allowUnbounded?: boolean;
}

export const ReportDateRangePicker: React.FC<ReportDateRangePickerProps> = ({
  start,
  end,
  setStart,
  setEnd,
  selectedRange,
  setSelectedRange,
  allowLockPeriod = false,
  allowUnbounded = false,
}) => {
  const startId = useId(), endId = useId(), rangeId = useId();
  const [isLockEnabled, setIsLockEnabled] = useState(false);
  const [isStartUnbounded, setIsStartUnbounded] = useState(start === "");
  const [isEndUnbounded, setIsEndUnbounded] = useState(end === "");
  // Define a list of preset ranges (with divider options).
  const dateRangeOptions: DateRangeOption[] = [
    { label: "---", isDivider: true },
    { label: "This month", start: startOfMonth(new Date()), end: endOfMonth(new Date()) },
    { label: "This quarter", start: startOfQuarter(new Date()), end: endOfQuarter(new Date()) },
    { label: "This financial year", start: startOfYear(new Date()), end: endOfYear(new Date()) },
    { label: "---", isDivider: true },
    {
      label: "Last month",
      start: startOfMonth(addDays(startOfMonth(new Date()), -1)),
      end: endOfMonth(addDays(startOfMonth(new Date()), -1)),
    },
    {
      label: "Last quarter",
      start: startOfQuarter(addDays(startOfQuarter(new Date()), -1)),
      end: endOfQuarter(addDays(startOfQuarter(new Date()), -1)),
    },
    {
      label: "Last financial year",
      start: startOfYear(addDays(startOfYear(new Date()), -365)),
      end: endOfYear(addDays(startOfYear(new Date()), -365)),
    },
    { label: "---", isDivider: true },
    {
      label: "Next month",
      start: startOfMonth(addMonths(new Date(), 1)),
      end: endOfMonth(addMonths(new Date(), 1)),
    },
    {
      label: "Next quarter",
      start: startOfQuarter(addMonths(new Date(), 3)),
      end: endOfQuarter(addMonths(new Date(), 3)),
    },
    {
      label: "Next financial year",
      start: startOfYear(addYears(new Date(), 1)),
      end: endOfYear(addYears(new Date(), 1)),
    },
    { label: "---", isDivider: true },
    { label: "Month to date", start: startOfMonth(new Date()), end: new Date() },
    { label: "Quarter to date", start: startOfQuarter(new Date()), end: new Date() },
    { label: "Year to date", start: startOfYear(new Date()), end: new Date() },
  ];

  // Auto�select (or clear) the preset dropdown based on the current start/end.
  useEffect(() => {
    const matchedPreset = dateRangeOptions.find((option) => {
      if (option.isDivider) return false;
      const optionStart = format(option.start, "yyyy-MM-dd");
      const optionEnd = format(option.end, "yyyy-MM-dd");
      return optionStart === start && optionEnd === end;
    });
    setSelectedRange(matchedPreset ? matchedPreset.label : "");
  }, [start, end, setSelectedRange, dateRangeOptions]);

  const handleDateRangeChange = (value: string) => {
    const range = dateRangeOptions.find((r) => r.label === value);
    if (!range || range.isDivider) return;
    setSelectedRange(range.label);
    setStart(format(range.start, "yyyy-MM-dd"));
    setEnd(format(range.end, "yyyy-MM-dd"));
  };

  // Navigate to previous/next period based on current date range
  const navigatePeriod = (direction: 'previous' | 'next') => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    // Special case: Full month(s) - from 1st to last day of month
    if (isFirstDayOfMonth(startDate) && isLastDayOfMonth(endDate)) {
      const monthsDiff = differenceInMonths(endDate, startDate) + 1;
      
      const newStartDate = direction === 'next'
        ? addMonths(startDate, monthsDiff)
        : subMonths(startDate, monthsDiff);
      
      const newEndDate = endOfMonth(addMonths(newStartDate, monthsDiff - 1));
      
      setStart(format(newStartDate, "yyyy-MM-dd"));
      setEnd(format(newEndDate, "yyyy-MM-dd"));
      setSelectedRange(""); // Clear preset selection
      return;
    }
    
    // Default case: Navigate by number of days (minimum 1 day if same dates)
    const daysDiff = Math.max(1, differenceInDays(endDate, startDate));
    
    const newStartDate = direction === 'next'
      ? addDays(startDate, daysDiff)
      : subDays(startDate, daysDiff);
    
    const newEndDate = addDays(newStartDate, differenceInDays(endDate, startDate));
    
    setStart(format(newStartDate, "yyyy-MM-dd"));
    setEnd(format(newEndDate, "yyyy-MM-dd"));
    setSelectedRange(""); // Clear preset selection
  };

  const handlePreviousPeriod = () => navigatePeriod('previous');
  const handleNextPeriod = () => navigatePeriod('next');

  // Helper function to adjust dates when they create invalid ranges
  const adjustDatesForValidRange = (newStart: string, newEnd: string, changedField: 'start' | 'end') => {
    const startDate = new Date(newStart);
    const endDate = new Date(newEnd);
    const currentStartDate = new Date(start);
    const currentEndDate = new Date(end);
    
    // Calculate current duration in days
    const currentDuration = differenceInDays(currentEndDate, currentStartDate);
    
    if (startDate > endDate) {
      if (changedField === 'start') {
        // Start was changed and goes after end - adjust end to maintain duration
        const adjustedEnd = addDays(startDate, currentDuration);
        setEnd(format(adjustedEnd, "yyyy-MM-dd"));
      } else {
        // End was changed and goes before start - adjust start to maintain duration
        const adjustedStart = subDays(endDate, currentDuration);
        setStart(format(adjustedStart, "yyyy-MM-dd"));
      }
    }
  };

  // Helper function to adjust dates when lock period is enabled
  const adjustDatesForLockedPeriod = (newDate: string, changedField: 'start' | 'end') => {
    const currentStartDate = new Date(start);
    const currentEndDate = new Date(end);
    const currentDuration = differenceInDays(currentEndDate, currentStartDate);
    
    if (changedField === 'start') {
      const newStartDate = new Date(newDate);
      const adjustedEnd = addDays(newStartDate, currentDuration);
      setEnd(format(adjustedEnd, "yyyy-MM-dd"));
    } else {
      const newEndDate = new Date(newDate);
      const adjustedStart = subDays(newEndDate, currentDuration);
      setStart(format(adjustedStart, "yyyy-MM-dd"));
    }
  };

  const handleStartChange = (newStart: string) => {
    if (isLockEnabled && allowLockPeriod) {
      adjustDatesForLockedPeriod(newStart, 'start');
      setStart(newStart);
    } else {
      setStart(newStart);
      if (!isEndUnbounded) {
        adjustDatesForValidRange(newStart, end, 'start');
      }
    }
    setSelectedRange(""); // Clear preset selection
  };

  const handleEndChange = (newEnd: string) => {
    if (isLockEnabled && allowLockPeriod) {
      adjustDatesForLockedPeriod(newEnd, 'end');
      setEnd(newEnd);
    } else {
      setEnd(newEnd);
      if (!isStartUnbounded) {
        adjustDatesForValidRange(start, newEnd, 'end');
      }
    }
    setSelectedRange(""); // Clear preset selection
  };

  const handleStartUnboundedChange = (unbounded: boolean) => {
    setIsStartUnbounded(unbounded);
    if (unbounded) {
      setStart("");
    } else {
      setStart(format(new Date(), "yyyy-MM-dd"));
    }
    setSelectedRange(""); // Clear preset selection
  };

  const handleEndUnboundedChange = (unbounded: boolean) => {
    setIsEndUnbounded(unbounded);
    if (unbounded) {
      setEnd("");
    } else {
      setEnd(format(new Date(), "yyyy-MM-dd"));
    }
    setSelectedRange(""); // Clear preset selection
  };

  const toggleLockPeriod = () => {
    setIsLockEnabled(!isLockEnabled);
  };

  return (
    <div className="flex flex-wrap gap-4">
      <div>
        <div className="flex justify-between items-center">
          <label htmlFor={startId}>Start</label>
          {allowUnbounded && (
            <span
              onClick={() => handleStartUnboundedChange(!isStartUnbounded)}
              title={isStartUnbounded ? "Unbounded start date - from beginning of time" : "Click to make start date unbounded"}
              style={{
                cursor: 'pointer',
                fontSize: '16px',
                userSelect: 'none',
                color: isStartUnbounded ? '#007bff' : '#6c757d'
              }}
            >
              ∞
            </span>
          )}
        </div>
        <input
          id={startId}
          className="form-control"
          type="date"
          value={start}
          onChange={(e) => handleStartChange(e.target.value)}
          disabled={isStartUnbounded}
          placeholder={isStartUnbounded ? "All time" : ""}
        />
      </div>
      <div>
        <div className="flex justify-between items-center">
          <label htmlFor={endId}>End</label>
          {allowUnbounded && (
            <span
              onClick={() => handleEndUnboundedChange(!isEndUnbounded)}
              title={isEndUnbounded ? "Unbounded end date - to future" : "Click to make end date unbounded"}
              style={{
                cursor: 'pointer',
                fontSize: '16px',
                userSelect: 'none',
                color: isEndUnbounded ? '#007bff' : '#6c757d'
              }}
            >
              ∞
            </span>
          )}
        </div>
        <input
          id={endId}
          className="form-control"
          type="date"
          value={end}
          onChange={(e) => handleEndChange(e.target.value)}
          disabled={isEndUnbounded}
          placeholder={isEndUnbounded ? "All time" : ""}
        />
      </div>
      {allowLockPeriod && (
        <div className="flex items-end">
          <span
            onClick={toggleLockPeriod}
            title={isLockEnabled ? "Duration locked - changes will maintain period length" : "Click to lock duration"}
            style={{
              cursor: 'pointer',
              fontSize: '20px',
              marginBottom: '8px',
              position: 'relative',
              display: 'inline-block',
              userSelect: 'none'
            }}
          >
            📅
            <span style={{
              position: 'absolute',
              top: '-2px',
              right: isLockEnabled ? '-1px' : '-2px',
              fontSize: '12px'
            }}>
              {isLockEnabled ? '🔒' : '🔓'}
            </span>
          </span>
        </div>
      )}
      <div>
        <label htmlFor={rangeId}>Date Range</label>
        <select
          id={rangeId}
          className="form-control"
          value={selectedRange}
          onChange={(e) => handleDateRangeChange(e.target.value)}
          disabled={isStartUnbounded || isEndUnbounded}
        >
          <option value="">Select a date range</option>
          {dateRangeOptions.map((rangeOption, idx) => {
            if (rangeOption.isDivider) {
              return (
                <option key={`divider-${idx}`} disabled value="---">
                  ---
                </option>
              );
            }
            return (
              <option key={rangeOption.label} value={rangeOption.label}>
                {rangeOption.label}
              </option>
            );
          })}
        </select>
      </div>
      <div className="flex items-end gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handlePreviousPeriod}
          title="Previous period"
          disabled={isStartUnbounded || isEndUnbounded}
        >
          ←
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleNextPeriod}
          title="Next period"
          disabled={isStartUnbounded || isEndUnbounded}
        >
          →
        </button>
      </div>
    </div>
  );
};
